/**
 * Rostering: map invoice service type name to funding type for shift creation and service categories.
 */

/**
 * Map invoice service type display name to funding type (NDIS | AGED_CARE | PRIVATE | OTHER).
 * Used when filtering shift "Service Category" options and for service-categories?funding_type=...
 *
 * @param {string} name - e.g. "Aged Care", "Core", "COS", "Allied Health"
 * @returns {"NDIS"|"AGED_CARE"|"PRIVATE"|"OTHER"}
 */
export function fundingTypeFromInvoiceServiceTypeName(name) {
  if (name == null || typeof name !== "string") return "NDIS";
  const normalized = name.trim().toLowerCase();
  if (normalized === "aged care") return "AGED_CARE";
  if (normalized === "private") return "PRIVATE";
  if (normalized === "other") return "OTHER";
  return "NDIS";
}

/** Funding types always available for a care sector on the shift screen. */
export function getMandatoryFundingTypes(careSector) {
  if (careSector === "aged_care") return ["AGED_CARE"];
  if (careSector === "both") return ["NDIS", "AGED_CARE"];
  return ["NDIS"];
}

/**
 * Effective shift-screen funding types from SP rostering settings
 * (mandatory sector types + configured shift_screen_funding_types).
 */
export function getShiftScreenFundingTypes(spRosteringSetting) {
  const configured = Array.isArray(spRosteringSetting?.shift_screen_funding_types)
    ? spRosteringSetting.shift_screen_funding_types
    : [];
  return Array.from(
    new Set([
      ...getMandatoryFundingTypes(spRosteringSetting?.care_sector),
      ...configured,
    ])
  );
}

export const FUNDING_TYPE_LABELS = {
  NDIS: "NDIS",
  AGED_CARE: "Aged Care",
  PRIVATE: "Private",
  OTHER: "Other",
};

/**
 * Filter service type options to those whose funding type is in the allowed list.
 * If allowedFundingTypes is empty, returns the full list (no filtering).
 *
 * @param {Array<{ label: string, value: number|string, funding_type?: string }>} serviceTypes - from roster-invoices/service-type
 * @param {string[]} allowedFundingTypes - from custom-line-items/allowed-funding-types
 * @returns {Array<{ label: string, value: number|string }>}
 */
export function filterServiceTypesByFundingType(serviceTypes, allowedFundingTypes) {
  if (!Array.isArray(serviceTypes) || serviceTypes.length === 0) return serviceTypes ?? [];
  if (!Array.isArray(allowedFundingTypes) || allowedFundingTypes.length === 0) return serviceTypes;
  const set = new Set(allowedFundingTypes.map((s) => String(s).toUpperCase()));
  return serviceTypes.filter((opt) => {
    const fundingType = opt?.funding_type
      ? String(opt.funding_type).toUpperCase()
      : fundingTypeFromInvoiceServiceTypeName(opt?.label);
    return set.has(fundingType);
  });
}

const FUNDING_TYPE_GROUPS = [
  { value: "NDIS", label: "NDIS Service Categories" },
  { value: "AGED_CARE", label: "Aged Care Service Categories" },
  { value: "PRIVATE", label: "Private Service Categories" },
  { value: "OTHER", label: "Other Service Categories" },
];

/**
 * Group service category options for react-select so users can distinguish NDIS,
 * Aged Care, Private and Other categories in the shift screen dropdown.
 *
 * @param {Array<{ label: string, value: number|string, funding_type?: string }>} serviceTypes
 * @returns {Array<{ label: string, options: Array }>}
 */
export function groupServiceTypesByFundingType(serviceTypes) {
  if (!Array.isArray(serviceTypes) || serviceTypes.length === 0) return [];

  const grouped = FUNDING_TYPE_GROUPS.map((group) => {
    const options = serviceTypes.filter((opt) => {
      const fundingType = opt?.funding_type
        ? String(opt.funding_type).toUpperCase()
        : fundingTypeFromInvoiceServiceTypeName(opt?.label);
      return fundingType === group.value;
    });

    return options.length ? { label: group.label, options } : null;
  }).filter(Boolean);

  const knownFundingTypes = new Set(FUNDING_TYPE_GROUPS.map((group) => group.value));
  const unknownOptions = serviceTypes.filter((opt) => {
    const fundingType = opt?.funding_type
      ? String(opt.funding_type).toUpperCase()
      : fundingTypeFromInvoiceServiceTypeName(opt?.label);
    return !knownFundingTypes.has(fundingType);
  });

  if (unknownOptions.length) {
    grouped.push({ label: "Other Service Categories", options: unknownOptions });
  }

  return grouped;
}

/**
 * Keep only service categories that have at least one support item / custom line.
 *
 * @param {Array<{ label: string, value: number|string }>} serviceTypes
 * @param {Set<string>|null} serviceTypeIdsWithLineItems
 * @returns {Array<{ label: string, value: number|string }>}
 */
export function filterServiceTypesWithLineItems(serviceTypes, serviceTypeIdsWithLineItems) {
  if (!Array.isArray(serviceTypes) || serviceTypes.length === 0) return serviceTypes ?? [];
  if (!serviceTypeIdsWithLineItems || serviceTypeIdsWithLineItems.size === 0) return [];
  return serviceTypes.filter((opt) => serviceTypeIdsWithLineItems.has(String(opt.value)));
}

